import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  orderBy,
  query,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import emailjs from "@emailjs/browser";

import { db } from "../../firebase";

import "./FeedbackNew.css";
import "../../shared/ui/Button/Button.css";

import Title from "../../shared/ui/Title/title";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

function FeedbackNew() {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [consentAccepted, setConsentAccepted] = useState(false); // Добавлено состояние для согласия

  const sliderRef = useRef(null);
  const sectionRef = useRef(null);

  const sendEmailNotification = async (name, text) => {
    const templateParams = {
      name: name,
      message: text,
    };

    try {
      await emailjs.send(
        "service_mc5zbyl",
        "template_iy79fc9",
        templateParams,
        "Sh9n2H9hAJA-6DyLW"
      );
      console.log("Email отправлен успешно");
    } catch (error) {
      console.error("Ошибка отправки email:", error);
    }
  };

  const submitReview = async () => {
    // Проверка на согласие с политикой
    if (!consentAccepted) {
      alert("Пожалуйста, примите условия Политики конфиденциальности");
      return;
    }

    if (!name || !text) {
      // Визуальная подсказка о незаполненных полях
      if (!name) document.querySelector('input[name="name"]')?.classList.add('error');
      if (!text) document.querySelector('textarea[name="review"]')?.classList.add('error');
      return;
    }

    setIsLoading(true);

    try {
      await addDoc(collection(db, "reviews"), {
        name,
        text,
        createdAt: serverTimestamp(),
        approved: false,
        consentGiven: true, // Добавлено поле о согласии
        consentDate: serverTimestamp(),
      });

      await sendEmailNotification(name, text);

      setName("");
      setText("");
      setConsentAccepted(false); // Сброс согласия после отправки
      await fetchReviews();

      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      alert("Ошибка при отправке отзыва. Попробуйте снова.");
      console.error("Ошибка:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReviews = async () => {
    const q = query(
      collection(db, "reviews"),
      where("approved", "==", true),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setReviews(data);

    const qPending = query(
      collection(db, "reviews"),
      where("approved", "==", false),
      orderBy("createdAt", "desc")
    );
    const pendingSnapshot = await getDocs(qPending);
    const pendingData = pendingSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setPendingReviews(pendingData);

    setTimeout(() => {
      if (sliderRef.current) {
        sliderRef.current.slickGoTo(0, true);
      }
      window.dispatchEvent(new Event("resize"));
    }, 100);
  };

  const approveReview = async (id) => {
    const reviewRef = doc(db, "reviews", id);
    await updateDoc(reviewRef, { approved: true });
    fetchReviews();
  };

  useEffect(() => {
    fetchReviews();

    // Анимация появления
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: reviews.length > 3,
    speed: 600,
    slidesToShow: Math.min(3, reviews.length),
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    beforeChange: (current, next) => setActiveSlide(next),
    appendDots: (dots) => (
      <div className="slider-dots">
        <ul className="custom-dots">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <button className={`dot ${i === activeSlide ? 'active' : ''}`}>
        <span className="dot-inner"></span>
      </button>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          autoplay: true,
        }
      }
    ],
  };

  // Функция для форматирования даты
  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) return '';
    
    const date = timestamp.toDate();
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Сегодня';
    if (diffDays === 1) return 'Вчера';
    if (diffDays < 7) return `${diffDays} дня(ей) назад`;
    
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // Убираем класс ошибки при вводе
  const handleInputChange = (e, setter) => {
    setter(e.target.value);
    e.target.classList.remove('error');
  };

  return (
    <section className="feedback" id="Feedback" ref={sectionRef}>
      
      {/* Декоративные элементы */}
      <div className="feedback-decoration">
        <div className="decoration-circle"></div>
        <div className="decoration-dots"></div>
      </div>

      <div className="feedback-container">
        <Title title="Отзывы" translate="Feedback" />

        {/* Слайдер с отзывами */}
          {reviews.length > 0 && (
            <div className="reviews-slider-section">
              <div className="slider-header">
                <h3 className="slider-title">
                  Что говорят мои клиенты
                  <span className="reviews-count">{reviews.length}</span>
                </h3>
              </div>

              <div className="reviews-slider-container">
                <Slider ref={sliderRef} {...sliderSettings}>
                  {reviews.map((review, index) => {
                    const date = formatDate(review.createdAt);
                    const initials = review.name
                      ?.split(' ')
                      .map(word => word[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <div key={review.id} className="slide-item">
                        <div className="review-card">
                          <div className="card-header">
                            <div className="reviewer-info">
                              <div className="avatar-wrapper">
                                <div className="avatar-gradient">
                                  <span className="avatar-initials">{initials || '👤'}</span>
                                </div>
                                <div className="avatar-status"></div>
                              </div>
                              <div className="reviewer-details">
                                <h4 className="reviewer-name">{review.name}</h4>
                                <div className="review-meta">
                                  <span className="review-date">{date}</span>
                                  <span className="review-badge">Проверенный</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="card-body">
                            <p className="review-text">{review.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </div>
          )}

          {/* Информационные сообщения */}
          <div className="info-messages-f">
            <div className="info-message-f">
              <span className="info-icon">ℹ️</span>
              <p>
                Оставьте отзыв и получите скидку <strong className="discount">5%</strong> на следующий заказ. 
                Сделайте скриншот и отправьте его мне при следующем заказе.
              </p>
            </div>
            
            {/* <div className="info-message old-reviews">
              <span className="info-icon">📚</span>
              <p>
                Так как я решила обновить раздел с отзывами, старые пришлось убрать. 
                С ними вы можете ознакомиться{" "}
                <a className="old-reviews-link" href="/old-feedback">
                  здесь
                  <span className="link-arrow">→</span>
                </a>
              </p>
            </div> */}
          </div>
        
        <div className="feedback-content">
          {/* Форма отправки отзыва */}
          <div className="review-form-wrapper">
            <div className="review-form-header">
              <h3 className="form-title">Оставьте свой отзыв</h3>
              <p className="form-subtitle">Ваше мнение важно для меня</p>
            </div>
            
            <div className="review-form">
              <div className="form-fields-s">
                <div className="input-group">
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => handleInputChange(e, setName)}
                    placeholder=" "
                    className="floating-input"
                    required
                  />
                  <label className="floating-label">Ваше имя</label>
                  {/* <span className="input-icon">👤</span> */}
                </div>
                
                <div className="input-group">
                  <textarea
                    name="message"
                    value={text}
                    onChange={(e) => handleInputChange(e, setText)}
                    placeholder=" "
                    className="floating-input"
                    rows="4"
                    required
                  />
                  <label className="floating-label">Ваш отзыв</label>
                  {/* <span className="input-icon">💬</span> */}
                </div>
              </div>

              {/* Блок согласия с политикой конфиденциальности */}
              <div className="consent-wrapper">
                <label className="consent-checkbox-label">
                  <input
                    type="checkbox"
                    checked={consentAccepted}
                    onChange={(e) => setConsentAccepted(e.target.checked)}
                    className="consent-checkbox"
                  />
                  <span className="consent-text">
                    Я соглашаюсь на обработку моих персональных данных в соответствии с{" "}
                    <a href="/policy" target="_blank" className="consent-link">
                      Политикой конфиденциальности
                    </a>
                  </span>
                </label>
              </div>

              <button
                className={`submit-button ${isSubmitted ? "submitted" : ""}`}
                onClick={submitReview}
                disabled={isLoading || isSubmitted || !consentAccepted}
              >
                {isLoading ? (
                  <div className="spinner-container">
                    <div className="spinner" />
                    <span>Отправка...</span>
                  </div>
                ) : isSubmitted ? (
                  <>
                    <span className="check-icon">✓</span>
                    <span>Отзыв отправлен</span>
                  </>
                ) : (
                  <>
                    <span>Отправить отзыв</span>
                  </>
                )}
              </button>
            </div>

            <div className="discount-badge">
              <div className="badge-content">
                <span className="badge-icon">🎁</span>
                <div className="badge-text">
                  <strong>Скидка 5%</strong>
                  <p>на следующий заказ за отзыв</p>
                </div>
              </div>
            </div>
          </div>

          
        </div>
      </div>
    </section>
  );
}

export default FeedbackNew;